import { useId, useState, type FormEvent } from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/business/icon'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

interface ActionDialogBaseProps {
  open: boolean
  title: string
  description?: string
  confirmText: string
  cancelText?: string
  pending?: boolean
  onOpenChange: (open: boolean) => void
}

interface ActionTextInputDialogProps extends ActionDialogBaseProps {
  initialValue: string
  inputLabel: string
  inputPlaceholder?: string
  onConfirm: (value: string) => void
}

interface ActionConfirmDialogProps extends ActionDialogBaseProps {
  tone?: 'default' | 'danger'
  iconName?: string
  onConfirm: () => void
}

const dialogPanelClass =
  'w-[420px] max-w-[calc(100vw-2rem)] rounded-[10px] border border-black/10 bg-white p-0 text-black shadow-[0_16px_44px_rgba(0,0,0,0.16)]'
const cancelButtonClass =
  'h-9 min-w-20 rounded-[9px] border border-[#e5e5e5] bg-white px-6 text-[14px] font-normal text-black shadow-none hover:bg-[#f7f7f7]'
const primaryButtonClass =
  'h-9 min-w-20 rounded-[9px] bg-[#0a84ff] px-6 text-[14px] font-normal text-white shadow-none hover:bg-[#0071e3]'
const dangerButtonClass =
  'h-9 min-w-20 rounded-[9px] bg-[#ff3b30] px-6 text-[14px] font-normal text-white shadow-none hover:bg-[#e9342a]'

export function ActionTextInputDialog({
  open,
  title,
  description,
  initialValue,
  inputLabel,
  inputPlaceholder,
  confirmText,
  cancelText = '取消',
  pending = false,
  onOpenChange,
  onConfirm,
}: ActionTextInputDialogProps) {
  const inputId = useId()
  const [value, setValue] = useState(initialValue)
  const normalizedValue = value.trim()

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!normalizedValue || pending) return
    if (normalizedValue === initialValue.trim()) {
      onOpenChange(false)
      return
    }
    onConfirm(normalizedValue)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogPanelClass} overlayClassName="bg-black/30 backdrop-blur-none">
        <form className="px-6 pt-7 pb-6" onSubmit={handleSubmit}>
          <DialogTitle className="mb-2 text-[16px] leading-6 font-normal text-black">
            {title}
          </DialogTitle>
          <DialogDescription
            className={description ? 'mb-3 text-[14px] leading-5 text-black/70' : 'sr-only'}
          >
            {description || inputLabel}
          </DialogDescription>
          <label className="sr-only" htmlFor={inputId}>
            {inputLabel}
          </label>
          <Input
            id={inputId}
            value={value}
            placeholder={inputPlaceholder}
            onChange={(event) => setValue(event.target.value)}
            disabled={pending}
            autoFocus
            className="h-8 rounded-[3px] border border-[#d0d0d0] bg-white px-3 py-0 text-[13px] text-black shadow-none focus-visible:border-[#0a84ff] focus-visible:ring-0"
          />
          <DialogFooter className="mt-6 flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className={cancelButtonClass}
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              {cancelText}
            </Button>
            <Button
              type="submit"
              className={primaryButtonClass}
              disabled={pending || !normalizedValue}
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function ActionConfirmDialog({
  open,
  title,
  description,
  confirmText,
  cancelText = '取消',
  pending = false,
  tone = 'default',
  iconName,
  onOpenChange,
  onConfirm,
}: ActionConfirmDialogProps) {
  const isDanger = tone === 'danger'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={dialogPanelClass}
        overlayClassName="bg-black/30 backdrop-blur-none"
      >
        <div className="px-6 pt-7 pb-6">
          <div className={cn('flex items-start', iconName ? 'gap-3' : 'gap-0')}>
            {iconName && (
              <Icon
                name={iconName}
                className={cn('mt-0.5 text-[18px]', isDanger ? 'text-[#ff9500]' : 'text-[#0a84ff]')}
              />
            )}
            <div>
              <DialogTitle className="text-[16px] leading-6 font-normal text-black">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="mt-4 text-[14px] leading-5 text-black">
                  {description}
                </DialogDescription>
              )}
            </div>
          </div>
          <DialogFooter className="mt-6 flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className={cancelButtonClass}
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              {cancelText}
            </Button>
            <Button
              type="button"
              variant={isDanger ? 'destructive' : 'default'}
              className={isDanger ? dangerButtonClass : primaryButtonClass}
              disabled={pending}
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
